import {
  DateTimeFormatter,
  usePremiumCurrencyFormatter,
} from '@wix/premium-react-text';
import * as React from 'react';
import { Trans, Translation } from 'react-i18next';
import { Text } from '@wix/design-system';
import {
  SwitchPlanType,
  RefundCapabilityType,
  Experiments,
} from '../../../../../enums';
import { ChangePlanModalContext } from '../../../ChangePlanModalContext';
import s from '../ChangePlanModal.scss';
import { RootStore, withInjectedRootStore } from '../../../../../providers';

interface SummaryPartProps {
  isRefundChangeType: boolean;
}

const SummaryPart: React.FC<SummaryPartProps> = ({
  isRefundChangeType,
  children,
}) => {
  const keys = isRefundChangeType
    ? {
        explanation: 'change_plan.change_plan_modal.refund_explanation',
        linkUrl: 'change_plan.change_plan_modal.refund_help_link_url',
        linkText: 'change_plan.change_plan_modal.refund_help_link_text',
      }
    : {
        explanation: 'change_plan.change_plan_modal.extend_explanation',
        linkUrl: 'change_plan.change_plan_modal.extend_help_link_url',
        linkText: 'change_plan.change_plan_modal.extend_help_link_text',
      };
  return (
    <Translation>
      {(t) => (
        <>
          <div className={s.summaryHeading}>
            <Text weight='bold'>{children}</Text>
          </div>
          <Text size='small'>{t(keys.explanation)}</Text>
          <br />
          <Text size='small'>
            <a target='_blank' href={t(keys.linkUrl)}>
              {t(keys.linkText)}
            </a>
          </Text>
          <br />
          <br />
        </>
      )}
    </Translation>
  );
};

interface ChangePlanModalSummaryCompProps {
  rootStore: RootStore;
}

export const ChangePlanModalSummaryComp: React.FC<
  ChangePlanModalSummaryCompProps
> = ({ rootStore }) => {
  const { changePlanDetails } = React.useContext(ChangePlanModalContext);
  const { experiments } = rootStore.appStore.appContext;
  const [currencyToString] = usePremiumCurrencyFormatter();

  const { daysAdded: days, newEndOfPeriod } = changePlanDetails;
  const isChangePlanExtendContentFixEnabled =
    experiments[Experiments.ChangePlanExtendContentFix] === 'true';

  const isRefundChangeType =
    changePlanDetails.changePlanType === SwitchPlanType.Refund &&
    changePlanDetails.refundCapabilityType !==
      RefundCapabilityType.REFUNDABLE_NEGATIVE_BALANCE;
  const isExtendChangeType =
    changePlanDetails.changePlanType === SwitchPlanType.Extend &&
    (isChangePlanExtendContentFixEnabled ? !!days : true);
  const isGracePeriodType =
    changePlanDetails.changePlanType === SwitchPlanType.GracePeriod;
  const amount = changePlanDetails.refundAmount
    ? currencyToString(changePlanDetails.refundAmount)
    : 0;
  const { renewalPrice, localizedProductName, endOfPeriod } =
    changePlanDetails.destinationProductDetails;
  const currentPlanName =
    changePlanDetails.currentPlanDetails.localizedProductName;

  const isNewEndOfPeriodForRenewalDate =
    experiments[Experiments.NewEndOfPeriodForRenewalDate] === 'true';

  const renewalDate = isNewEndOfPeriodForRenewalDate
    ? newEndOfPeriod ?? endOfPeriod
    : endOfPeriod;

  return (
    <DateTimeFormatter
      dateFormat='longMonth'
      render={(formatDate) => (
        <>
          {isRefundChangeType && (
            <SummaryPart isRefundChangeType={true}>
              <Trans
                i18nKey='change_plan.change_plan_modal.refund_amount'
                values={{ amount }}
              >
                You will be refunded
                <span className={s.success}>{{ amount }}</span>
              </Trans>
            </SummaryPart>
          )}
          {isExtendChangeType && (
            <SummaryPart isRefundChangeType={false}>
              <Trans
                i18nKey='change_plan.change_plan_modal.extend_amount'
                values={{ days }}
              >
                Your Premium Plan will be extended by
                <span className={s.success}>{{ days }}</span> days
              </Trans>
            </SummaryPart>
          )}
          {isGracePeriodType ? (
            <>
              <div className={s.gracePeriodMsg}>
                <Text size='small' data-testid='grace_period_message'>
                  <Trans
                    i18nKey='change_plan.change_plan_modal.grace_message'
                    values={{ plan: currentPlanName }}
                  ></Trans>
                </Text>
              </div>
              <Text size='small'>
                <Trans i18nKey='change_plan.change_plan_modal.continue_to_grace'>
                  regular<b>bold</b>
                </Trans>
              </Text>
            </>
          ) : (
            <Text size='small' dataHook='change-plan-next-renewal'>
              <Trans
                i18nKey='change_plan.change_plan_modal.next_renewal'
                values={{
                  toPlan: localizedProductName,
                  renewalDate: formatDate(renewalDate),
                  renewalAmount: currencyToString(renewalPrice),
                }}
              >
                Your new <span>{{ toPlan: localizedProductName }}</span> Plan
                will renew on{' '}
                <span>{{ renewalDate: formatDate(renewalDate) }}</span> for{' '}
                <span>{{ renewalAmount: currencyToString(renewalPrice) }}</span>
              </Trans>
            </Text>
          )}
        </>
      )}
    />
  );
};

export const ChangePlanModalSummary = withInjectedRootStore(
  ChangePlanModalSummaryComp,
);
